<template>
	<v-row>
		<v-col>
			<v-form
					v-model="formIsValid.description"
					ref="descriptionForm"
					class="mb-8"
			>
				<ui-box
						:form="false"
						new-forms
						:status="null"
				>
					<form-title title="About Your Pass">
						Enter a compelling description for your pass here, highlighting its key
						features, benefits, and any unique attractions or experiences it offers.
						This description will be visible to passholders, helping them understand
						the value and opportunities your pass provides. Be clear, concise, and
						engaging to encourage interest and excitement.
					</form-title>

					<form-divider inner/>

					<!-- Mobile Pass Description -->
					<v-row>
						<v-col>
							<wysiwyg-input
									v-model="localPass.mobile_pass_description"
									label="Pass Description"
									outlined
									:counter="2000"
									rows="4"
							></wysiwyg-input>
						</v-col>
					</v-row>
					<!-- End: Mobile Pass Description -->

				</ui-box>
			</v-form>

			<form-title title="Pass Customization">
				Customize what displays when pass holders open your pass.
			</form-title>

			<form-divider/>

			<v-form
					v-model="formIsValid.images"
					ref="imageForm"
					class="mb-8"
			>
				<ui-box
						:form="false"
						:status="hasImage ? 'success' : null"
						style="max-width: 738;"
				>
					<!-- Advanced Image -->
					<form-title
						tag="p"
						title="Mobile Pass Header Image"
						titleClass="black--text subtitle-2"
					>
						This image will be used to showcase your pass when users view it within
						the Mobile Pass app.
					</form-title>
					<v-row no-gutters>
						<bw-image
								v-model="localPass.media[heroIndex]"
								:key="`media-${localPass.media[heroIndex].id}`"
								class="rounded-lg"
								:cols="{ lg: 6, md: 6, sm: 6 }"
								:upload-assignment="'deal'"
								:upload-assignment-id="localPass.id"
						></bw-image>
					</v-row>
					<p class="caption mt-2 mb-0">Recommended minimum size: 1280 x 640 @ 2X</p>
					<!-- End: Advanced Image -->
				</ui-box>
			</v-form>
			<template v-if="showAdvanced">
				<v-form
						v-model="formIsValid.video"
						ref="videoForm"
						class="mb-8"
				>
					<ui-box
							:form="false"
							:status="hasImage ? 'success' : null"
							style="max-width: 738;"
					>
						<form-title
							tag="p"
							title="Video"
							titleClass="black--text subtitle-2"
						></form-title>
						<v-select
								v-model="localPass.video.host"
								class="rounded-lg"
								:items="[
									{ name: 'Youtube Embed URL (Recommended)', value: 'youtube' },
									{ name: 'Vimeo Embed URL', value: 'vimeo' },
									{ name: 'Upload File', value: 'cloudinary' }
								]"
								item-text="name"
								label="Video Type"
								outlined
								style="width: 60%"
								@change="resetVideo"
						></v-select>
						<form-divider class="mb-8 mt-2"/>
						<template v-if="localPass.video.host === 'cloudinary'">
							<v-row no-gutters>
								<bw-image
										v-model="localPass.video"
										:auto-show-uploader="false"
										class="rounded-lg"
										:cols="{ lg: 6, md: 6, sm: 6 }"
										enable-tabs="video"
										show-remove
										type="video"
										:upload-assignment="'deal'"
										:upload-assignment-id="localPass.id"
										@delete="onVideoDelete"
								></bw-image>
							</v-row>
							<p class="caption mt-2">Your file must be an MP4 and must be smaller than 500mb.</p>
						</template>
						<template v-else>
							<v-row no-gutters>
								<v-col class="pb-1">
									<v-text-field
											v-model="localPass.video.caption"
											class="rounded-lg"
											hint=""
											label="Title"
											outlined
											persistent-hint
									></v-text-field>
								</v-col>
							</v-row>
							<v-row no-gutters>
								<v-col class="pb-1">
									<v-text-field
											v-model="localPass.video.url"
											class="rounded-lg"
											hint=""
											:label="`${Bandwango.capitalize(localPass.video.host)} Embed URL`"
											outlined
											persistent-hint
											:rules="rules.mediaUrl"
											@change="
												delete localPass.video.id;
												delete localPass.video.media_id
											"
									></v-text-field>
								</v-col>
							</v-row>
						</template>
					</ui-box>
				</v-form>

				<v-form
						v-model="formIsValid.audio"
						ref="videoForm"
						class="mb-8"
				>
					<ui-box
							:form="false"
							:status="hasImage ? 'success' : null"
							style="max-width: 738;"
					>
						<form-title
							tag="p"
							title="Audio Clip"
							titleClass="black--text subtitle-2"
						></form-title>
						<v-row no-gutters class="mt-8">
							<v-col class="pb-1">
								<v-text-field
										v-model="localPass.audio.caption"
										class="rounded-lg"
										hint=""
										label="Title"
										outlined
										persistent-hint
								></v-text-field>
							</v-col>
						</v-row>
						<v-row no-gutters>
							<bw-image
									v-model="localPass.audio"
									:auto-show-uploader="false"
									class="rounded-lg"
									:cols="{ lg: 6, md: 6, sm: 6 }"
									enable-tabs="audio"
									show-remove
									type="audio"
									:upload-assignment="'deal'"
									:upload-assignment-id="localPass.id"
									@delete="onAudioDelete"
							></bw-image>
						</v-row>
						<p class="caption mt-2">Your file must be MP3, OGG, WAV, or M4A formats and smaller than 500 mb.</p>
					</ui-box>
				</v-form>
			</template>
			<v-btn
					class="mb-8 me-2 me-md-4 rounded-lg"
					color="secondary"
					depressed
					outlined
					small
					v-if="enableAdvancedFeatures"
					@click="showAdvanced = !showAdvanced"
			>
				<v-icon
						:class="{
							'me-2': $vuetify.breakpoint.smAndUp,
							'ms-n1': $vuetify.breakpoint.smAndUp
						}"
				>
					mdi-eye-outline
				</v-icon>
				<span>{{ showAdvanced ? 'Hide' : 'Show' }} Advanced</span>
			</v-btn>
			<save-form
					:refs="$refs"
					:additional-validations="[hasData]"
					:is-saving="isSaving"
					new-forms
					@cancel-action="exit"
					@save-action="save"
			></save-form>

		</v-col>
	</v-row>
</template>

<script>
// import customizationMixin from '../Customization/Mixins/customizationMixin';

const emptyAudio = { title: '', subtitle: '', host: 'cloudinary', location: 'audio' };
const emptyVideo = { caption: '', host: 'youtube', location: 'video', url: ''};

export default {
	name: 'PassBasicDetails',
	// mixins: [customizationMixin],
	props: {
		checkInTemplates: {
			type: Array,
			default: () => []
		},
		currentSection: { type: String, default: '' },
		extraData: { type: Object, default: () => ({}) },
		partner: { type: Object, default: null },
		pass: { type: Object, default: () => ({}) },
		returnUrl: { type: String, default: '' },
		template: { type: Object, default: () => ({}) },
		user: { type: Object, default: () => ({}) },
	},
	data() {
		return {
			formIsValid: {
				audio: false,
				images: false,
				description: false,
				video: false
			},
			isSaving: false,
			localPass: {},
			rules: {
				mobile_pass_description: [v => !!v || 'Description is required.'],
				mediaUrl: [
					v => {
						const protocolRegex = /^https?:\/\//;
						return !v ||
								protocolRegex.test(v) ||
								'Please include the whole video url including http(s)://'
					},
					v => {
						const fullRegex = /^https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b[-a-zA-Z0-9()@:%_+.~#?&\/=]*$/;
						return (!v || (!!v && fullRegex.test(v))) || 'Video url is invalid.'
					}
				]
			},
			showAdvanced: false
		};
	},
	created() {
		this.localPass = cloneDeep(this.pass);

		if (this.heroIndex === -1) {
			this.localPass.media.push({
				location: 'Hero',
				name: 'Hero',
				url: '/images/placehold.co.png',
				cropperSettings: {
					limitAR: '2:1',
					x: 0,
					y: 0
				}
			});
		} else {
			this.$set(this.localPass.media, this.heroIndex, {
				...this.localPass.media[this.heroIndex],
				cropperSettings: {
					limitAR: '2:1',
					x: 0,
					y: 0
				}
			});
		}
		this.sections = this.extraData.sections;

		this.$set(this.localPass, 'pass_settings', {
			...(this.localPass?.pass_settings || {})
		});

		const video = this.localPass.media.find(x => x.location === 'video');
		this.$set(
			this.localPass,
			'video',
			video ?? {  ...emptyVideo }
		);

		const audio = this.localPass.media.find(x => x.location === 'audio');
		this.$set(
			this.localPass,
			'audio',
			audio ?? { ...emptyAudio }
		);

		// Trigger reactivity here
		this.localPass = { ...this.localPass };
	},
	computed: {
		enableAdvancedFeatures() {
			return this.isAdmin || this.$bwFeatures.eb_advanced_features;
		},
		hasData() {
			return this.hasImage || this.localPass.mobile_pass_description;
		},
		hasImage() {
			return !!this.localPass?.media?.length && !this.localPass.media[0].url
					.includes('/images/placehold.co.png');
		},
		heroIndex() {
			return this.localPass.media.findIndex( x => x.location === 'Hero');
		},
		isAdmin() {
			return !!this.user?.is_admin;
		},
		videoIndex() {
			return this.localPass.media.findIndex( x => x.location === 'video');
		}
	},
	methods: {
		exit() {
			window.location.href = `/partner-portal/${this.partner.id}/passes`;
		},

		onAudioDelete() {
			this.$set( this.localPass, 'audio', { ...emptyAudio });
		},

		onVideoDelete() {
			this.$set( this.localPass, 'video', { ...emptyVideo });
		},

		resetVideo() {
			const {
					localPass,
					localPass: { video: { host } },
					videoIndex
			} = this;
			localPass.video = { ...emptyVideo };
			localPass.video.host = host;

			if (videoIndex !== -1) {
				localPass.media.splice(this.videoIndex, 1);
			}
		},
		save() {
			this.isSaving = true;

			if (!this.localPass.audio?.url) this.localPass.audio = null;

			if (!this.localPass.video?.url) this.localPass.video = null;

			let url = `/partner-portal/${this.partner.id}/passes/save-pass/${this.pass.id || ''}`;

			axios.put(url, this.localPass)
					.then(response => {
						setTimeout(() => {
							window.location = `/partner-portal/${this.partner.id}/passes/${this.pass.id}/places`;
						}, 2000);
					})
					.catch(error => {
						this.isSaving = false;
						window.EventBus.$emit('system-messages', [
							{ message: error.message, messageType: 'error' }
						]);
					})
					.finally(() => this.isSaving = false);
		}
	}
};
</script>
